"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Loader, MapPin, Phone, Mail, Clock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Contact() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact" ref={sectionRef} className={`py-24 bg-surface transition-all duration-300 ${sectionVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-primary mb-4">START A CONVERSATION</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Reach out to discuss your next development opportunity or real estate strategy.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <FormProvider className="w-full" formId="contact_form_home" sectionName="contact" defaultValues={{
          name: "",
          email: "",
          phone: "",
          company: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 20
          }
        }} submitText="Send Message" successMessage="Thank you for reaching out. We'll respond within 24 hours.">
            <ContactFormContent />
          </FormProvider>

          <div className="space-y-6">
            <Card className="card">
              <CardContent className="flex items-start gap-4 pt-6">
                <div className="p-3 rounded-md bg-accent/10">
                  <MapPin className="size-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">Office Location</h3>
                  <p className="text-muted-foreground text-sm">10602 Little Wind Cove, Austin, TX 78730</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card">
              <CardContent className="flex items-start gap-4 pt-6">
                <div className="p-3 rounded-md bg-accent/10">
                  <Phone className="size-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">Phone</h3>
                  <p className="text-muted-foreground text-sm">+1 (512) 565-0499</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card">
              <CardContent className="flex items-start gap-4 pt-6">
                <div className="p-3 rounded-md bg-accent/10">
                  <Mail className="size-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">Email</h3>
                  <p className="text-muted-foreground text-sm">contact@dealforgere.com</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card">
              <CardContent className="flex items-start gap-4 pt-6">
                <div className="p-3 rounded-md bg-accent/10">
                  <Clock className="size-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">Business Hours</h3>
                  <p className="text-muted-foreground text-sm">Monday–Friday: 9am–6pm</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="card">
        <CardContent className="py-12 text-center">
          <div className="inline-flex items-center justify-center size-16 rounded-full bg-accent/10 mb-4">
            <Mail className="size-8 text-accent" />
          </div>
          <h3 className="text-primary font-semibold mb-2">Message Sent Successfully</h3>
          <p className="text-muted-foreground mb-6">{successMessage}</p>
          <Button onClick={resetForm} variant="outline">
            Send Another Message
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="card">
      <CardContent className="pt-6">
        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <FormField control={control} name="name" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" className="focus-visible:border-accent transition-all duration-200" {...field} />
                  </FormControl>
                  <FormDescription>First and last name</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" className="focus-visible:border-accent transition-all duration-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            pattern: {
              value: /^[\d\s\-\+\(\)]+$/,
              message: "Please enter a valid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 123-4567" className="focus-visible:border-accent transition-all duration-200" {...field} />
                  </FormControl>
                  <FormDescription>International format preferred</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="company" render={({
            field
          }) => <FormItem>
                  <FormLabel>Company / Organization</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Development Ltd." className="focus-visible:border-accent transition-all duration-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="message" rules={{
            required: "Message is required",
            minLength: {
              value: 20,
              message: "Message must be at least 20 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Tell us how we can assist you..." className="min-h-32 focus-visible:border-accent transition-all duration-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 text-sm">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="btn-primary w-full">
              {isSubmitting ? <>
                  <Loader className="animate-spin size-4" />
                  Sending...
                </> : 'Send Message'}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}